import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import Layout from "../components/Layout";
import StatusBadge from "../components/StatusBadge";
import { Card } from "../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft } from "lucide-react";
import { formatDateTime } from "../lib/format";

export default function RecruiterProductivity() {
  const { email } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [recruiters, setRecruiters] = useState([]);

  useEffect(() => {
    api.get(`/productivity/recruiter/${encodeURIComponent(email)}`).then(({ data }) => setData(data));
    if (user?.role === "super_custodian" || user?.role === "tl") {
      api.get("/productivity/recruiters").then(({ data }) => setRecruiters(data));
    }
  }, [email, user?.role]);

  if (!data) return <Layout><div className="text-slate-500">Loading…</div></Layout>;

  return (
    <Layout>
      <button onClick={() => nav("/productivity")} className="text-sm text-slate-500 hover:text-slate-900 mb-4 flex items-center gap-1">
        <ArrowLeft className="w-4 h-4" />Back to team
      </button>

      <div className="flex items-end justify-between gap-4 flex-wrap mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-slate-900" data-testid="recruiter-name">{data.name || data.email}</h1>
          <p className="text-slate-500 mt-1">{data.email} {data.tl_email && <span>· Reports to <span className="font-medium text-slate-700">{data.tl_email}</span></span>}</p>
        </div>
        {recruiters.length > 0 && (
          <div className="min-w-[260px]">
            <Select value={email} onValueChange={(v) => nav(`/productivity/recruiter/${encodeURIComponent(v)}`)}>
              <SelectTrigger data-testid="recruiter-switcher"><SelectValue /></SelectTrigger>
              <SelectContent>
                {recruiters.map((r) => <SelectItem key={r.email} value={r.email}>{r.name || r.email}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-5 border-slate-200">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-400">Total candidates</div>
          <div className="font-display text-3xl font-bold text-slate-900 mt-2">{data.total_candidates}</div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-400">Last sync</div>
          <div className="font-display text-lg font-semibold text-slate-900 mt-2">{data.recent_syncs[0] ? formatDateTime(data.recent_syncs[0].ts) : "—"}</div>
        </Card>
        <Card className="p-5 border-slate-200">
          <div className="text-xs uppercase tracking-wider font-semibold text-slate-400">Updates (last 20)</div>
          <div className="font-display text-3xl font-bold text-slate-900 mt-2">{data.recent_updates.length}</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6 border-slate-200">
          <h3 className="font-display font-semibold text-lg text-slate-900 mb-4">Candidate status distribution</h3>
          <div className="space-y-3">
            {Object.entries(data.pipeline).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between" data-testid={`status-${k.toLowerCase().replace(/\s+/g, "-")}`}>
                <StatusBadge status={k} />
                <span className="font-display text-lg font-semibold text-slate-900">{v}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 border-slate-200">
          <h3 className="font-display font-semibold text-lg text-slate-900 mb-4">Activity by date (last 30d)</h3>
          {data.activity_by_date.length === 0 && <p className="text-sm text-slate-400">No activity in the last 30 days.</p>}
          <div className="space-y-2 max-h-72 overflow-y-auto scrollbar-thin">
            {data.activity_by_date.slice().reverse().map((a) => (
              <div key={a.date} className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{a.date}</span>
                <span className="font-medium text-emerald-600">+{a.added}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6 border-slate-200 lg:col-span-2">
          <h3 className="font-display font-semibold text-lg text-slate-900 mb-4">Recent syncs</h3>
          {data.recent_syncs.length === 0 && <p className="text-sm text-slate-400">No syncs yet.</p>}
          <div className="divide-y divide-slate-100">
            {data.recent_syncs.map((s) => (
              <div key={s.id} className="py-3 flex items-center justify-between text-sm">
                <div className="text-xs text-slate-500">{formatDateTime(s.ts)}</div>
                <div className="flex gap-4 text-xs">
                  <span className="text-emerald-600 font-medium">+{s.added}</span>
                  <span className="text-indigo-600 font-medium">{s.updated} updated</span>
                  <span className="text-amber-600 font-medium">{s.rejected_count} rejected</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </Layout>
  );
}
